import { useEffect } from 'react';
import { VENDOR_DARK_CLASS } from './VendorDarkModeBridge';

/** Chakra's own body classes (`color-mode.utils.cjs`, `classNames`). */
const CHAKRA_DARK_CLASS = 'chakra-ui-dark';
const CHAKRA_LIGHT_CLASS = 'chakra-ui-light';

/** Marks the transition-suppressing `<style>` so the test can see it. */
export const PRINT_LIGHT_MODE_STYLE_ATTR = 'data-print-light-mode';

/**
 * Prints in light mode, whatever mode the screen is in.
 *
 * `theme/styles.ts` paints `bg.canvas` / `text.primary` on html and body.
 * Browsers do not print backgrounds by default, so a dark-mode user who hits
 * Cmd-P gets dark mode's near-white ink on white paper (about 1.05:1): a page
 * that comes out of the printer effectively blank. `ColorModePreferenceSync`
 * follows the OS scheme, which makes dark mode the common case, not a niche.
 *
 * On `beforeprint`, if the document is resolved dark, this flips exactly the
 * three markers Chakra 2.10.9 itself writes when the mode changes
 * (`getColorModeUtils` in `color-mode.utils.cjs`: `setDataset` and
 * `setClassName`) plus the `dark` class `VendorDarkModeBridge` adds for the
 * vendored TipTap sheets, and on `afterprint` puts back what it found.
 *
 * Why synchronous DOM writes rather than `setColorMode('light')`: the print
 * renderer lays out the page as soon as the `beforeprint` handlers return.
 * `setColorMode` does write the dataset synchronously, but the React commit
 * it also schedules is not guaranteed to land before that layout, and the
 * restore on `afterprint` would race the same way. Writing the DOM directly
 * is deterministic, and it is the same set of writes Chakra performs.
 *
 * Why nothing touches `chakra-ui-color-mode` in localStorage: Chakra re-reads
 * that key on the next load, so flipping it for a print would turn "print this
 * note" into "switch me to light mode permanently" for anyone who closes the
 * tab from the print dialog. The same reason `setColorMode` is off the table:
 * it calls `colorModeManager.set`. (`ColorModePreferenceSync` would now heal a
 * flipped key from `bower-color-mode-preference`, which is the real persisted
 * choice — but only for an explicit Light/Dark preference, and only on the
 * next load. Leaving the key alone is still the cheaper guarantee.)
 *
 * The transition kill mirrors Chakra's `preventTransition`: `styles.ts` puts
 * a 0.2s colour transition on every element, and a transition that starts at
 * `beforeprint` would hand the print renderer its START value, which is the
 * dark ink this exists to avoid.
 *
 * Renders nothing. Mount once, inside `ChakraProvider`.
 */
export function PrintLightMode(): null {
  useEffect(() => {
    const root = document.documentElement;
    const body = document.body;
    // Non-null while a print we flipped is in progress.
    let restore: (() => void) | null = null;

    const onBeforePrint = () => {
      // Also what makes a second `beforeprint` (Chrome fires it again when
      // the dialog re-renders) a no-op: the document is light by then, so
      // it cannot snapshot the flipped state as the thing to restore.
      if (root.dataset.theme !== 'dark') return;

      const previousColorScheme = root.style.colorScheme;
      const hadChakraDark = body.classList.contains(CHAKRA_DARK_CLASS);
      const hadChakraLight = body.classList.contains(CHAKRA_LIGHT_CLASS);
      const hadVendorDark = root.classList.contains(VENDOR_DARK_CLASS);

      const noTransition = document.createElement('style');
      noTransition.setAttribute(PRINT_LIGHT_MODE_STYLE_ATTR, '');
      noTransition.textContent = '*{transition:none!important}';
      document.head.appendChild(noTransition);

      root.dataset.theme = 'light';
      root.style.colorScheme = 'light';
      body.classList.add(CHAKRA_LIGHT_CLASS);
      body.classList.remove(CHAKRA_DARK_CLASS);
      root.classList.remove(VENDOR_DARK_CLASS);

      restore = () => {
        root.dataset.theme = 'dark';
        root.style.colorScheme = previousColorScheme;
        if (hadChakraDark) body.classList.add(CHAKRA_DARK_CLASS);
        if (!hadChakraLight) body.classList.remove(CHAKRA_LIGHT_CLASS);
        if (hadVendorDark) root.classList.add(VENDOR_DARK_CLASS);
        noTransition.remove();
      };
    };

    // `afterprint` with no matching `beforeprint` (or after a light-mode
    // print) has nothing to undo.
    const onAfterPrint = () => {
      restore?.();
      restore = null;
    };

    window.addEventListener('beforeprint', onBeforePrint);
    window.addEventListener('afterprint', onAfterPrint);
    return () => {
      onAfterPrint();
      window.removeEventListener('beforeprint', onBeforePrint);
      window.removeEventListener('afterprint', onAfterPrint);
    };
  }, []);

  return null;
}
